import { critiqueSourceGameWithModel, reviseSourceGameFromEvidence, type PlaytestCritique } from "./autonomous-critic";
import { generateSourceGameWithModel, type ModelJsonRequest, type ModelJsonRequester, type ModelJsonResult } from "./generation-pipeline";
import type { CarcadeGameSpec } from "./game-spec";
import type { PlaytestEvidence } from "./playtest-evidence";

const OPENROUTER_AUTH_URL = "https://openrouter.ai/auth";
const OPENROUTER_KEY_EXCHANGE_URL = "https://openrouter.ai/api/v1/auth/keys";
const OPENROUTER_CHAT_URL = "https://openrouter.ai/api/v1/chat/completions";

export const DEFAULT_CREATE_MODEL = "openai/gpt-5.2";
export const OPENROUTER_CALLBACK_PATH = "/openrouter/callback";
export const OPENROUTER_CODE_MESSAGE = "carcade-openrouter-code";

function base64Url(bytes: Uint8Array): string {
  let value = "";
  for (const byte of bytes) value += String.fromCharCode(byte);
  return btoa(value).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function createPkcePair(): Promise<{ readonly verifier: string; readonly challenge: string }> {
  const random = new Uint8Array(32);
  crypto.getRandomValues(random);
  const verifier = base64Url(random);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return { verifier, challenge: base64Url(new Uint8Array(digest)) };
}

export function openRouterAuthorizationUrl(origin: string, challenge: string): string {
  const url = new URL(OPENROUTER_AUTH_URL);
  url.searchParams.set("callback_url", `${origin}${OPENROUTER_CALLBACK_PATH}`);
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("code_challenge_method", "S256");
  return url.toString();
}

export function completeOpenRouterPopupCallback(): boolean {
  if (typeof window === "undefined" || window.location.pathname !== OPENROUTER_CALLBACK_PATH) return false;
  const code = new URLSearchParams(window.location.search).get("code");
  if (code && window.opener) window.opener.postMessage({ type: OPENROUTER_CODE_MESSAGE, code }, window.location.origin);
  window.document.body.textContent = code ? "Connecting your OpenRouter account…" : "OpenRouter did not return a connection code.";
  if (code) window.setTimeout(() => window.close(), 50);
  return true;
}

interface KeyExchangeResponse { readonly key?: unknown }

export async function exchangeOpenRouterCode(code: string, verifier: string, fetcher: typeof fetch = fetch): Promise<string> {
  const response = await fetcher(OPENROUTER_KEY_EXCHANGE_URL, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code, code_verifier: verifier, code_challenge_method: "S256" }),
  });
  const body = await response.json() as KeyExchangeResponse;
  if (!response.ok || typeof body.key !== "string" || body.key.length < 20) throw new Error("OpenRouter could not connect this account");
  return body.key;
}

interface ChatResponse {
  readonly id?: unknown;
  readonly usage?: unknown;
  readonly choices?: readonly { readonly message?: { readonly content?: unknown } }[];
}

export interface GeneratedGameSpec {
  readonly spec: CarcadeGameSpec;
  readonly receipt: {
    readonly model: string;
    readonly responseId?: string;
    readonly usage?: unknown;
  };
}

export interface GeneratedRevision {
  readonly spec: CarcadeGameSpec;
  readonly receipt: {
    readonly model: string;
    readonly responseId?: string;
    readonly usage?: unknown;
  };
}

function parseModelJson(body: ChatResponse, phase: ModelJsonRequest["phase"], responseOk: boolean): ModelJsonResult {
  const content = body.choices?.[0]?.message?.content;
  if (!responseOk || typeof content !== "string") throw new Error(`OpenRouter did not return the ${phase} result`);
  let candidate: unknown;
  try { candidate = JSON.parse(content); }
  catch { throw new Error(`OpenRouter returned invalid JSON during ${phase}`); }
  return {
    candidate,
    ...(typeof body.id === "string" ? { responseId: body.id } : {}),
    ...(body.usage === undefined ? {} : { usage: body.usage }),
  };
}

function maxTokensForPhase(phase: ModelJsonRequest["phase"]): number {
  if (phase === "architect") return 4_000;
  if (phase === "critic") return 4_000;
  return 24_000;
}

function messageContent(request: ModelJsonRequest): string | readonly Record<string, unknown>[] {
  if (!request.images?.length) return request.prompt;
  return [
    { type: "text", text: request.prompt },
    ...request.images.map((url) => ({ type: "image_url", image_url: { url } })),
  ];
}

export function openRouterJsonRequester(apiKey: string, model: string = DEFAULT_CREATE_MODEL, fetcher: typeof fetch = fetch): ModelJsonRequester {
  return async (request) => {
    const response = await fetcher(OPENROUTER_CHAT_URL, {
      method: "POST",
      headers: {
        authorization: `Bearer ${apiKey}`,
        "content-type": "application/json",
        "http-referer": typeof location === "undefined" ? "https://carcade.party" : location.origin,
        "x-title": `Carcade Create ${request.phase}`,
      },
      body: JSON.stringify({
        model,
        messages: [{ role: "user", content: messageContent(request) }],
        response_format: { type: "json_object" },
        max_tokens: maxTokensForPhase(request.phase),
        temperature: request.temperature,
      }),
    });
    const body = await response.json() as ChatResponse;
    return parseModelJson(body, request.phase, response.ok);
  };
}

export async function generateGameSpec(
  apiKey: string,
  brief: string,
  model: string = DEFAULT_CREATE_MODEL,
  fetcher: typeof fetch = fetch,
): Promise<GeneratedGameSpec> {
  const generated = await generateSourceGameWithModel(brief, openRouterJsonRequester(apiKey, model, fetcher));
  const last = generated.phases.at(-1);
  return {
    spec: generated.spec,
    receipt: {
      model,
      ...(last?.responseId === undefined ? {} : { responseId: last.responseId }),
      usage: { pipeline: "source-game-v1", phases: generated.phases },
    },
  };
}

export async function critiqueGeneratedGame(
  apiKey: string,
  brief: string,
  spec: CarcadeGameSpec,
  evidence: PlaytestEvidence,
  model: string = DEFAULT_CREATE_MODEL,
  fetcher: typeof fetch = fetch,
): Promise<PlaytestCritique> {
  return critiqueSourceGameWithModel(brief, spec, evidence, openRouterJsonRequester(apiKey, model, fetcher));
}

export async function reviseGeneratedGame(
  apiKey: string,
  brief: string,
  spec: CarcadeGameSpec,
  evidence: PlaytestEvidence,
  critique: PlaytestCritique,
  model: string = DEFAULT_CREATE_MODEL,
  fetcher: typeof fetch = fetch,
): Promise<GeneratedRevision> {
  let lastResult: ModelJsonResult | undefined;
  const requester: ModelJsonRequester = async (request) => {
    const result = await openRouterJsonRequester(apiKey, model, fetcher)(request);
    lastResult = result;
    return result;
  };
  const revised = await reviseSourceGameFromEvidence(brief, spec, evidence, critique, requester);
  return {
    spec: revised,
    receipt: {
      model,
      ...(lastResult?.responseId === undefined ? {} : { responseId: lastResult.responseId }),
      usage: {
        pipeline: "autonomous-playtest-v1",
        critique: { passed: critique.passed, score: critique.score, problems: critique.problems },
      },
    },
  };
}
